import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { Navigate, useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { SignupFormValues, useSignup } from '@context/SignupContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isTickmillSCType } from '@utils/companyName.utils'

import { isAccountAppropriatenessTestSubmittedStatus } from '@model/AccountDetailedStatus'
import { AccountType } from '@model/AccountType'
import { TickmillProductType } from '@model/TickmillProductType'

import { safeLocalStorage } from '@hooks/useStorage'

import { FormValuesLegalDocs } from '../Pages/PersonalDetails/PersonalDetailsDocumentsFactory'
import { PersonalDetailsStep1Page } from '../Pages/PersonalDetails/PersonalDetailsStep1/PersonalDetailsStep1Page'
import { PersonalDetailsStepUSPage } from '../Pages/PersonalDetails/PersonalDetailsStep1/PersonalDetailsStepUSPage'
import { PersonalDetailsStep2Page } from '../Pages/PersonalDetails/PersonalDetailsStep2/PersonalDetailsStep2Page'
import { PersonalDetailsStep3Page } from '../Pages/PersonalDetails/PersonalDetailsStep3/PersonalDetailsStep3Page'
import { LeadCreationPage } from '../Pages/PersonalDetails/PersonalDetailsStep4/LeadCreationPage'
import { PersonalDetailsStep4Page } from '../Pages/PersonalDetails/PersonalDetailsStep4/PersonalDetailsStep4Page'
import { ResendEmailPage } from '../Pages/PersonalDetails/PersonalDetailsStep4/ResendEmailPage'
import { SignupErrorPage } from '../Pages/PersonalDetails/PersonalDetailsStep4/SignupErrorPage'
import { VerifyEmailPage } from '../Pages/PersonalDetails/PersonalDetailsStep4/VerifyEmailPage'
import {
  PersonalDetailsStep5FormValues,
  PersonalDetailsStep5Page,
} from '../Pages/PersonalDetails/PersonalDetailsStep5/PersonalDetailsStep5Page'
import { PersonalDetailsStep6Page } from '../Pages/PersonalDetails/PersonalDetailsStep6/PersonalDetailsStep6Page'
import { VerifyPhoneNumberPage } from '../Pages/PersonalDetails/VerifyPhoneNumber/VerifyPhoneNumberPage'
import { isStocksOnlyLead } from '../hooks/productValidationUtils'
import { useCreateClient } from '../hooks/useCreateClient'
import { useProducts } from '../hooks/useProducts'

export type StepPath = 'basic' | 'residence' | 'contact' | 'info' | 'password' | 'documents'

const NavigateWithSearch: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}`} />
}

export const usePersonalDetailsRoutes = (): RouteConfig[] => {
  const navigate = useNavigate()
  const { appendValues, signupData } = useSignup()
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { denormalize, signInAndMoveToFinancialInfo } = useCreateClient()
  const location = useLocation()
  const entity = useSessionEntity()
  const { setSelectedProductIds } = useProducts()

  useEffect(() => {
    const currentStep = location.pathname.split('/').pop()
    const redirectSteps: StepPath[] = ['residence', 'contact']
    const navigationEntry = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming
    const isReload = navigationEntry?.type === 'reload'
    if (isReload && redirectSteps.includes(currentStep as StepPath)) {
      navigate(`/sign-up/personal-details/basic${location.search}`, { replace: true })
    }
  }, [])

  const standardValues = useCallback(
    (values: FormValuesLegalDocs) =>
      ({
        ...signupData.values,
        agreedLegalDocuments: values?.agreedLegalDocuments || [],
        marketDataClassificationId: values?.readDocumentMarketData?.marketDataClassificationId,
        shouldReceiveNewsletter: signupData.lead?.optIn,
      } as SignupFormValues),
    [signupData.lead?.optIn, signupData.values]
  )

  const expediteValues = useCallback(
    (values: PersonalDetailsStep5FormValues) =>
      ({
        ...signupData.values,
        ...values,
        shouldReceiveNewsletter: signupData.lead?.optIn,
      } as SignupFormValues),
    [signupData.lead?.optIn, signupData.values]
  )

  const handleSubmit = useCallback(
    async (data: SignupFormValues) => {
      appendValues(data, true)
      if (!signupData.lead) {
        return navigate('/sign-up/personal-details/error')
      }
      if (signupData.lead.type.id === AccountType.Individual) {
        const newData = denormalize(signupData.lead, data)
        await apiClient.createClient(newData)
        await signInAndMoveToFinancialInfo(data)
        safeLocalStorage.removeItem('lead')
      } else {
        navigate('/sign-up/company-details/')
      }
    },
    [apiClient, appendValues, denormalize, navigate, signInAndMoveToFinancialInfo, signupData.lead]
  )

  const includeDocuments = useMemo(
    () =>
      !(
        isTickmillSCType(entity) ||
        isTickmillSCType(signupData.lead?.tickmillCompany?.id) ||
        isTickmillSCType(signupData.values.tickmillCompanyId)
      ),
    [entity, signupData.lead?.tickmillCompany?.id, signupData.values.tickmillCompanyId]
  )

  const stepPaths = useMemo<readonly StepPath[]>(() => {
    const paths: StepPath[] = ['basic', 'residence', 'contact', 'info', 'password']
    if (includeDocuments) {
      paths.push('documents')
    }
    return paths
  }, [includeDocuments])

  const nextStep = useCallback(
    async (values?: Partial<SignupFormValues>) => {
      if (values) {
        const leadProductIds = signupData.lead?.products?.map((p) => p.id) || []
        const existingProductIds = (signupData.values?.productIds as TickmillProductType[]) || []
        let validatedProductIds = values.productIds || existingProductIds

        if (
          validatedProductIds.length > 0 &&
          leadProductIds.length > 0 &&
          !isStocksOnlyLead(leadProductIds)
        ) {
          validatedProductIds = validatedProductIds.filter((id) => leadProductIds.includes(id))

          if (validatedProductIds.length === 0) {
            validatedProductIds = leadProductIds
          }
        }

        appendValues({ ...values, productIds: validatedProductIds })
        setSelectedProductIds(validatedProductIds)
      }
      if (values?.isUsCitizen) {
        appendValues({ ...signupData.values, isUsCitizen: undefined })
        return navigate(`us-citizen${location.search}`)
      }

      const current = location.pathname.split('/').filter(Boolean).pop() as StepPath | undefined
      const idx = current ? stepPaths.indexOf(current) : -1
      const nextPath = stepPaths[idx + 1] ?? stepPaths[0]
      navigate(`/sign-up/personal-details/${nextPath}${location.search}`)
    },
    [
      appendValues,
      signupData.values,
      signupData.lead?.products,
      navigate,
      location.pathname,
      location.search,
      stepPaths,
      setSelectedProductIds,
    ]
  )

  const subSteps: RouteConfig<StepPath>[] = useMemo(
    () => [
      {
        path: 'basic',
        element: <PersonalDetailsStep1Page onSubmit={nextStep} />,
        children: [{ path: 'us-citizen', element: <PersonalDetailsStepUSPage /> }],
      },
      { path: 'residence', element: <PersonalDetailsStep2Page onSubmit={nextStep} /> },
      {
        path: 'contact',
        element: <PersonalDetailsStep3Page />,
        children: [
          { path: 'verify-phone/:leadId', element: <VerifyPhoneNumberPage /> },
          {
            path: 'verify-email',
            element: <VerifyEmailPage />,
            children: [
              { path: 'resend', element: <ResendEmailPage /> },
              { path: 'confirm', element: <LeadCreationPage /> },
            ],
          },
          { path: 'error', element: <SignupErrorPage /> },
        ],
      },
      { path: 'info', element: <PersonalDetailsStep4Page onSubmit={nextStep} /> },
      {
        path: 'password',
        element: (
          <PersonalDetailsStep5Page
            onSubmit={(values) =>
              signupData.totalSteps.personalDetails < 6
                ? handleSubmit(expediteValues(values))
                : nextStep(values)
            }
          />
        ),
      },
      ...(includeDocuments
        ? [
            {
              path: 'documents',
              element: (
                <PersonalDetailsStep6Page
                  onSubmit={(values) => handleSubmit(standardValues(values))}
                />
              ),
            } as RouteConfig<StepPath>,
          ]
        : []),
    ],
    [
      nextStep,
      includeDocuments,
      signupData.totalSteps.personalDetails,
      handleSubmit,
      expediteValues,
      standardValues,
    ]
  )

  return useMemo(
    (): RouteConfig[] => [
      {
        path: 'personal-details',
        element: <NavigateWithSearch to={`/sign-up/personal-details/${subSteps[0].path}`} />,
        condition: !account,
        children: [
          ...subSteps,
          {
            path: '*',
            element: <NavigateWithSearch to={`/sign-up/personal-details/${subSteps[0].path}`} />,
          },
        ],
      },
      {
        path: 'personal-details/*',
        element: <NavigateWithSearch to={`/sign-up/financial-info/${account?.id || ''}`} />,
        condition: account && isAccountAppropriatenessTestSubmittedStatus(account.status.id),
      },
    ],
    [account, subSteps]
  )
}
